import React, { useMemo } from "react";
import { AnalysisResult } from "../../contracts/scoring";
import { ToneEQVisualizer } from "../../components/ToneEQVisualizer";
import { MFCCVisualizer } from "../../components/MFCCVisualizer";
import { AdviceCard } from "../../components/SmartTrainer/AdviceCard";

type MetricCard = { label: string; value: string; hint?: string };

const safeNum = (n: any) => {
  const x = Number(n);
  return Number.isFinite(x) ? x : null;
};

const fmt = (n: any, decimals = 0) => {
  const x = safeNum(n);
  if (x === null) return "—";
  return decimals > 0 ? x.toFixed(decimals) : String(Math.round(x));
};

const NoDataPanel: React.FC<{ reason: string }> = ({ reason }) => (
  <div className="bg-white/[0.02] border border-white/10 rounded-[48px] p-10 text-center">
    <div className="text-3xl mb-4">🔒</div>
    <div className="text-[10px] font-black uppercase tracking-[0.35em] text-slate-500">
      NO=100% — DONNÉE DSP ABSENTE
    </div>
    <div className="mt-3 text-white font-black text-xl">Timbre verrouillé</div>
    <div className="mt-3 text-slate-300 text-sm italic">{reason}</div>
    <div className="mt-6 text-[10px] text-slate-500">
      Aucun fallback n’est autorisé : l’onglet n’affiche rien tant que la preuve DSP réelle n’est pas présente.
    </div>
  </div>
);

export const ToneTab: React.FC<{
  result: AnalysisResult;
  lang: "en" | "fr";
  currentLevelId: number;
}> = ({ result, lang, currentLevelId }) => {
  /**
   * ✅ NO=100% (aligné UI policy)
   * Preuve DSP = result.tone.details + session.metrics.tone
   * MFCC brut (mfccData) peut être absent -> on n'empêche plus l'affichage,
   * mais on affiche un bandeau "trace indisponible".
   */
  const details: any = (result.tone as any)?.details;
  const mfcc = (result.tone as any)?.mfccData;
  const hasMfccTrace = Array.isArray(mfcc) && mfcc.length > 0;

  if (!details) {
    return <NoDataPanel reason="Détails timbre absents (result.tone.details manquant)." />;
  }

  const score = safeNum(result.session.metrics?.tone) ?? 0;
  const articulation = safeNum(result.session.metrics?.articulation);

  const diagnostics = Array.isArray(result.feedback?.diagnostics)
    ? result.feedback.diagnostics.filter((d: any) => d?.domain === "TONE")
    : [];

  const metrics: MetricCard[] = useMemo(() => {
    const cards: MetricCard[] = [];
    const dbg: any = result.session.metrics?.debug || {};

    // 1) Score (réel, pas dérivé)
    cards.push({
      label: "Tone score",
      value: `${fmt(score)}%`,
      hint: "Plus haut = mieux",
    });

    // 2) Articulation (réelle si présente)
    if (articulation !== null) {
      cards.push({
        label: "Attack match",
        value: `${fmt(articulation)}%`,
        hint: "Influence le rendu du timbre",
      });
    }

    // 3) Détails timbre (preuve DSP)
    if (safeNum(details?.mfccDistance) !== null) {
      cards.push({
        label: "MFCC distance",
        value: fmt(details.mfccDistance, 2),
        hint: "Plus bas = plus proche",
      });
    }

    if (safeNum(details?.similarity) !== null) {
      const s = safeNum(details.similarity)!;
      const pct = s <= 1.001 ? Math.round(s * 100) : Math.round(s);
      cards.push({
        label: "Similarity",
        value: `${Math.max(0, Math.min(100, pct))}%`,
        hint: "Plus haut = mieux",
      });
    }

    if (
      safeNum(details?.spectralCentroidRef) !== null &&
      safeNum(details?.spectralCentroidStu) !== null
    ) {
      cards.push({
        label: "Centroid Δ",
        value: `${Math.round(Math.abs(details.spectralCentroidStu - details.spectralCentroidRef))} Hz`,
        hint: "Écart de brillance",
      });
    }

    // 4) Trace audio debug (si exposée)
    if (safeNum(dbg?.rms_db) !== null) {
      cards.push({
        label: "RMS",
        value: `${fmt(dbg.rms_db, 1)} dB`,
        hint: "Niveau moyen",
      });
    }

    return cards.slice(0, 6);
  }, [result, score, articulation, details]);

  const scoreColor = score > 75 ? "text-emerald-400" : "text-cyan-400";

  return (
    <div className="flex flex-col gap-10 animate-fade-in pb-12">
      {/* Bandeau si MFCC brut indisponible (mais preuve DSP OK via details) */}
      {!hasMfccTrace && (
        <div className="mx-2 p-4 rounded-[24px] border border-orange-500/30 bg-orange-500/5 text-orange-300">
          <p className="text-[10px] font-black uppercase tracking-widest">Trace MFCC indisponible</p>
          <p className="text-[10px] opacity-80">
            L’analyse timbre est affichée via <b>tone.details</b> (preuve DSP), mais la trace brute{" "}
            <b>tone.mfccData</b> n’a pas été exportée.
          </p>
        </div>
      )}

      {/* 1) SCORE + POURQUOI + CONSEILS */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4 bg-[#02080a] p-10 rounded-[48px] border border-white/10 shadow-2xl flex flex-col items-center text-center justify-center gap-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent"></div>
          <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.4em] italic">
            Spectral Fidelity
          </span>
          <div className="flex items-baseline gap-2">
            <span className={`text-8xl font-black italic tracking-tighter ${scoreColor}`}>
              {Math.round(score)}
            </span>
            <span className="text-xl font-bold text-slate-700">/100</span>
          </div>
          <div className="px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-[9px] font-black text-slate-400 uppercase tracking-widest italic">
            {score > 80 ? "Master Tone Match" : "Divergence Spectrale"}
          </div>
        </div>

        <div className="lg:col-span-8 bg-black/40 p-8 rounded-[48px] border border-white/10 flex flex-col justify-center gap-6 shadow-inner">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-cyan-500/20 flex items-center justify-center text-2xl">
              🎸
            </div>
            <h4 className="text-sm font-black text-white uppercase italic tracking-widest">
              Analyse du Grain
            </h4>
          </div>

          <p className="text-lg text-slate-300 italic leading-relaxed pl-4 border-l-2 border-cyan-500/40">
            "Votre timbre est l'empreinte de votre instrument et de votre attaque. L'IA compare la distribution
            de vos fréquences par rapport au modèle Master."
          </p>

          <div className="space-y-3">
            {diagnostics.length > 0 ? (
              diagnostics.map((adv: any, i: number) => (
                <AdviceCard key={i} advice={adv} isHighContrast={false} />
              ))
            ) : (
              <div className="p-6 bg-white/5 rounded-3xl text-center">
                <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">
                  Équilibre Fréquentiel Optimal
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 2) MESURES DÉTECTÉES */}
      <div className="bg-white/[0.02] border border-white/10 p-8 rounded-[48px] space-y-6">
        <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.3em]">
          Mesures détectées
        </h4>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {metrics.map((m, i) => (
            <div key={i} className="bg-black/40 border border-white/10 rounded-2xl p-5">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">
                {m.label}
              </div>
              <div className="text-2xl font-black italic text-white tabular-nums">{m.value}</div>
              {m.hint ? <div className="text-[9px] mt-2 italic text-slate-500">{m.hint}</div> : null}
            </div>
          ))}
        </div>
      </div>

      {/* 3) MODULES */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-full min-h-[500px]">
        <ToneEQVisualizer details={details} lang={lang} levelId={currentLevelId} />
        <MFCCVisualizer
          attackMatch={articulation !== null ? articulation / 100 : undefined}
          lang={lang}
        />
      </div>
    </div>
  );
};
